import { motion } from "framer-motion";

const NODES = {
    india: { x: 442, y: 332, label: "INDIA" },
    uk: { x: 208, y: 118, label: "UNITED KINGDOM" },
    gcc: { x: 282, y: 302, label: "GCC" },
    africa: { x: 248, y: 452, label: "AFRICA" },
    centralAsia: { x: 402, y: 98, label: "CENTRAL ASIA" },
};

const ARCS = [
    { key: "uk", d: `M ${NODES.india.x} ${NODES.india.y} Q 300 170 ${NODES.uk.x} ${NODES.uk.y}`, node: NODES.uk, delay: 0.9 },
    { key: "gcc", d: `M ${NODES.india.x} ${NODES.india.y} Q 352 288 ${NODES.gcc.x} ${NODES.gcc.y}`, node: NODES.gcc, delay: 1.1 },
    { key: "africa", d: `M ${NODES.india.x} ${NODES.india.y} Q 322 432 ${NODES.africa.x} ${NODES.africa.y}`, node: NODES.africa, delay: 1.3 },
    { key: "centralAsia", d: `M ${NODES.india.x} ${NODES.india.y} Q 434 195 ${NODES.centralAsia.x} ${NODES.centralAsia.y}`, node: NODES.centralAsia, delay: 1.5 },
];

const NodeLabel = ({ x, y, text, anchor = "middle" }) => (
    <g transform={`translate(${x}, ${y})`}>
        <rect x={anchor === "middle" ? -(text.length * 3.6 + 12) : 4} y={-11} width={text.length * 7.2 + 24} height={22} rx={11} fill="white" fillOpacity="0.92" stroke="#E2E8F0" />
        <text x={anchor === "middle" ? 0 : 16} y={4} textAnchor={anchor} fontSize="9.5" letterSpacing="1.6" fill="#475569" fontFamily="Outfit, sans-serif" fontWeight="600">
            {text}
        </text>
    </g>
);

export default function HeroMap() {
    return (
        <svg viewBox="0 0 640 560" className="w-full h-auto" role="img" aria-label="Global healthcare corridors connecting India to the GCC, United Kingdom, Africa and Central Asia" data-testid="hero-corridor-map">
            <defs>
                <linearGradient id="gmArc" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#0A2540" />
                    <stop offset="100%" stopColor="#14B8A6" />
                </linearGradient>
                <radialGradient id="gmGlow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.16" />
                    <stop offset="100%" stopColor="#14B8A6" stopOpacity="0" />
                </radialGradient>
            </defs>

            <circle cx="392" cy="292" r="252" fill="url(#gmGlow)" />

            {/* abstract globe */}
            <g stroke="#DCE6F2" fill="none" strokeWidth="1.2">
                <circle cx="392" cy="292" r="205" />
                <ellipse cx="392" cy="292" rx="205" ry="72" />
                <ellipse cx="392" cy="292" rx="205" ry="140" />
                <ellipse cx="392" cy="292" rx="86" ry="205" />
                <ellipse cx="392" cy="292" rx="152" ry="205" />
            </g>
            <g fill="#C6D6E8">
                {[
                    [250, 190], [300, 150], [360, 130], [470, 150], [520, 200], [545, 280],
                    [530, 380], [480, 440], [400, 470], [310, 450], [240, 390], [215, 300],
                    [330, 240], [420, 230], [470, 320], [350, 370], [290, 330], [440, 400],
                ].map(([cx, cy], i) => (
                    <circle key={i} cx={cx} cy={cy} r="2.2" />
                ))}
            </g>

            {/* arcs */}
            {ARCS.map((a) => (
                <g key={a.key}>
                    <motion.path
                        d={a.d}
                        fill="none"
                        stroke="url(#gmArc)"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeDasharray="0 0"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 1 }}
                        transition={{ duration: 1.6, delay: a.delay, ease: [0.16, 1, 0.3, 1] }}
                    />
                    <motion.g
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: a.delay + 1.2, duration: 0.4 }}
                    >
                        <motion.circle
                            cx={a.node.x}
                            cy={a.node.y}
                            r="5"
                            fill="none"
                            stroke="#14B8A6"
                            strokeWidth="1.5"
                            animate={{ r: [5, 16], opacity: [0.7, 0] }}
                            transition={{ duration: 2.4, repeat: Infinity, delay: a.delay + 1.4, ease: "easeOut" }}
                        />
                        <circle cx={a.node.x} cy={a.node.y} r="4.5" fill="#0D9488" />
                    </motion.g>
                </g>
            ))}

            {/* India origin */}
            <motion.g initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}>
                <motion.circle
                    cx={NODES.india.x}
                    cy={NODES.india.y}
                    r="9"
                    fill="none"
                    stroke="#0A2540"
                    strokeWidth="1.5"
                    animate={{ r: [9, 30], opacity: [0.5, 0] }}
                    transition={{ duration: 2.8, repeat: Infinity, ease: "easeOut" }}
                />
                <circle cx={NODES.india.x} cy={NODES.india.y} r="7" fill="#0A2540" />
                <circle cx={NODES.india.x} cy={NODES.india.y} r="2.6" fill="#14B8A6" />
            </motion.g>

            <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2, duration: 0.8 }}>
                <NodeLabel x={NODES.india.x} y={NODES.india.y + 34} text={NODES.india.label} />
                <NodeLabel x={NODES.uk.x} y={NODES.uk.y - 22} text={NODES.uk.label} />
                <NodeLabel x={NODES.gcc.x} y={NODES.gcc.y - 24} text={NODES.gcc.label} />
                <NodeLabel x={NODES.africa.x} y={NODES.africa.y + 30} text={NODES.africa.label} />
                <NodeLabel x={NODES.centralAsia.x} y={NODES.centralAsia.y - 22} text={NODES.centralAsia.label} />
            </motion.g>
        </svg>
    );
}
